import React, { memo, useEffect, useRef, useState } from "react";
import { makeStyles, withStyles } from "tss-react/mui";
import { Theme, useMediaQuery } from "@mui/material";
import Typography from "@mui/material/Typography";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { useTranslation } from "react-i18next";
import { hourPixelHeight } from "./Scheduler";
import ScheduleEntryForm from "./SchedulerEntry/ScheduleEntryForm";
import Mission from "../../../interfaces/robot/missions/mission";
import ScheduleEntry, { getScheduleTimesAndDays } from "../../../interfaces/robot/scheduling/schedule-entry";
import TimeSpan, { getStringFromTimeSpan, getTimeSpanDecimalHours } from "../../../utils/time-span";
import Weekday, { getIndexFromWeekday, getWeekdayLabel } from "../../../utils/weekday";
import { useSharedActions, useSharedDispatch, useSharedSelector } from "../../../store/SharedProvider";
import {
  selectSelectedRoomId,
  selectAllCleaningZonesIdName,
  selectAllLandmarksIdName,
  selectMissionsRestSet,
} from "../../../store/room/room-selectors";
import { getNavigatorDescription } from "../../../interfaces/robot/navigator";
import { getColorFromString } from "../../../utils/mission-color";
import { LightTooltipWithBorder } from "../../General/Tooltips";
import { NavigatorName } from "../../../enums/command-hub/navigator";
import ZoneControllerMouseState from "../../../enums/robot/zone-planner/zone-controller-mouse-state";
import SchedulerControllerMouseState from "../../../enums/robot/rooms/scheduler-controller-mouse-state";
import {
  errorResetResponse,
  isErrorSetOpen,
  isSuccessSetOpen,
  successResetResponse,
} from "../../../interfaces/api/rest-set";
import { selectIsWebApp } from "../../../store/app-configurations/app-configurations-selectors";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FilterKeys } from "../../../enums/query-params/filters";
import SphereRobotTabs from "../../../enums/query-params/sphere-robot-tabs";

const WarningIcon = withStyles(WarningAmberRoundedIcon, (theme) => ({
  root: {
    color: theme.palette.warning.light,
  },
}));

const useStyles = makeStyles()((theme) => ({
  schedulerBox: {
    position: "absolute",
    marginLeft: theme.spacing(1),
    width: `calc(${100 / 7}% - ${theme.spacing(2)})`,
    marginRight: theme.spacing(1),
    borderRadius: "2px",
    borderLeftWidth: "4px",
    borderLeftStyle: "solid",
    cursor: "pointer",
  },
  zoneLinkButton: {
    display: "block",
    paddingLeft: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
  },
  zoneLinkButtonActive: {
    textDecorationLine: "underline",
    cursor: "pointer",
    "&:hover": {
      fontWeight: "bold",
    },
  },
  isDragging: {
    width: `calc(${100 / 7}% - ${theme.spacing(1)})`,
    borderRadius: 0,
  },
  hoveredScheduleEntryDivDot: {
    display: "inline",
    position: "absolute",
    height: theme.spacing(1),
    width: theme.spacing(1),
    backgroundColor: "#FFFFFF",
    borderRadius: "50%",
    border: `2px solid ${theme.palette.primary.dark}`,
  },
  hoveredScheduleEntryDivBottom: {
    left: theme.spacing(1),
    bottom: theme.spacing(-0.5),
  },
  hoveredScheduleEntryDivTop: {
    right: theme.spacing(1),
    top: theme.spacing(-0.5),
  },
  scheduleEntryResizeDiv: {
    width: "100%",
    height: "4px",
    position: "absolute",
    left: "0",
    backgroundColor: "rgba(255,255,255,0)",
    cursor: "ns-resize",
    zIndex: 2,
    pointerEvents: "auto",
  },
  scheduleEntryResizeTopDiv: {
    top: "0",
  },
  scheduleEntryResizeBottomDiv: {
    bottom: "0",
  },
  invalidEntry: {
    borderLeft: `4px solid ${theme.palette.error.main}`,
    background: "rgba(249,185,181, 0.5)",
  },
  scheduleDisabled: {
    background: "rgba(189,189,189, 0.5)",
    borderLeft: `4px solid ${theme.palette.grey[700]}`,
  },
  maintenanceSchedule: {
    borderLeft: `4px solid ${theme.palette.grey[700]}`,
    background:
      "repeating-linear-gradient(45deg, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5) 10px, #ffff99 10px, #ffff99 20px);",
  },
}));

const ScheduleEntryTypography = withStyles(Typography, (theme) => ({
  root: {
    color: theme.palette.primary.main,
    fontWeight: "bold",
    fontSize: "0.75rem",
    padding: "2px",
  },
}));

const ScheduleEntrySubTypography = withStyles(Typography, (theme) => ({
  root: {
    color: theme.palette.primary.main,
    fontSize: "0.75rem",
  },
}));

interface ScheduleBoxPositions {
  top: number;
  columnsLeft: number;
  height: number;
  tooltipPlacement: "right" | "top" | "bottom";
  includesStart: boolean;
  includesEnd: boolean;
}

export const getScheduleBoxPositionsFromSchedule = (
  timeFrom: TimeSpan,
  weekdayFrom: Weekday,
  timeTo: TimeSpan,
  weekdayTo: Weekday,
) => {
  const scheduleBoxTimes = getScheduleTimesAndDays(timeFrom, weekdayFrom, timeTo, weekdayTo);
  const schedulePositions: ScheduleBoxPositions[] = [];
  scheduleBoxTimes.forEach((t) => {
    const weekdayIndex = getIndexFromWeekday(t.weekday);
    schedulePositions.push({
      columnsLeft: weekdayIndex - 1,
      includesStart: t.weekday === weekdayFrom && t.timeFrom === timeFrom,
      includesEnd: t.weekday === weekdayTo && t.timeTo === timeTo,
      height: parseInt(hourPixelHeight, 10) * (getTimeSpanDecimalHours(t.timeTo) - getTimeSpanDecimalHours(t.timeFrom)),
      top: parseInt(hourPixelHeight, 10) * getTimeSpanDecimalHours(t.timeFrom),
      tooltipPlacement: t.timeFrom.hours >= 22 ? "top" : t.timeTo.hours < 2 ? "bottom" : "right",
    });
  });
  return schedulePositions;
};

const DRAG_THRESHOLD = 5;

const ScheduleEntryBox: React.FC<{
  schedule: ScheduleEntry;
  isTemporary: boolean;
  hasBeenEdited: boolean;
  isOverlapping: boolean;
  wasDragged: boolean;
  mission?: Mission;
}> = ({ schedule, isTemporary, hasBeenEdited, isOverlapping, wasDragged, mission }) => {
  const { cx, classes } = useStyles();
  const { t } = useTranslation();
  const matchesSmUp = useMediaQuery((theme: Theme) => theme.breakpoints.up("sm"));
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setFilterParams] = useSearchParams();

  const dispatch = useSharedDispatch();
  const navigate = useNavigate();

  const {
    setSelectedZone,
    setSelectedLandmark,
    setZonePlannerMouseState,
    setSelectedScheduleEntry,
    deleteDraftScheduleEntry,
    patchScheduleEntry,
    updateDraftScheduleEntry,
    patchMissionsRestSet,
  } = useSharedActions();

  const cleaningZonesIdName = useSharedSelector((state) => selectAllCleaningZonesIdName(state));
  const landmarksIdName = useSharedSelector((state) => selectAllLandmarksIdName(state));
  const currentRoomId = useSharedSelector((state) => selectSelectedRoomId(state));
  const missionsRestSet = useSharedSelector((state) => selectMissionsRestSet(state));
  const isWebApp = useSharedSelector((state) => selectIsWebApp(state));

  const [isHovering, setIsHovering] = useState(false);
  const [isDetailsViewOpen, setIsDetailsViewOpen] = useState(isTemporary);
  const startPos = useRef<{ x: number; y: number } | null>(null);
  const dragging = useRef(false);
  const [preDragSnapshot, setPreDragSnapshot] = useState<{
    timeFrom: TimeSpan;
    timeTo: TimeSpan;
    weekdayFrom: Weekday;
    weekdayTo: Weekday;
  } | null>(null);

  useEffect(() => {
    const mouseUpTouchEndHandler = () => {
      dragging.current = false;
      startPos.current = null;
    };

    window.addEventListener("mouseup", mouseUpTouchEndHandler);
    window.addEventListener("touchend", mouseUpTouchEndHandler);

    return () => {
      window.removeEventListener("mouseup", mouseUpTouchEndHandler);
      window.removeEventListener("touchend", mouseUpTouchEndHandler);
    };
  }, [dispatch]);

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    startPos.current = {
      x: e.clientX,
      y: e.clientY,
    };

    dragging.current = false;

    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!startPos.current || dragging.current) return;

    const dx = e.clientX - startPos.current.x;
    const dy = e.clientY - startPos.current.y;

    const distance = Math.sqrt(dx * dx + dy * dy);

    if (distance > DRAG_THRESHOLD) {
      dragStartHandler();
    }
  };

  const dragStartHandler = () => {
    dragging.current = true;
    setPreDragSnapshot({
      timeFrom: schedule.timeFrom,
      timeTo: schedule.timeTo,
      weekdayFrom: schedule.weekdayFrom,
      weekdayTo: schedule.weekdayTo,
    });
    dispatch(
      setSelectedScheduleEntry({
        entry: schedule,
        mouseState: SchedulerControllerMouseState.DragSchedule,
        day: null,
        time: null,
      }),
    );
  };

  const mouseOverHandler = () => {
    setIsHovering(true);
  };

  const mouseLeaveHandler = () => {
    setIsHovering(false);
  };

  const mouseUpTouchEndHandler = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging.current) {
      setIsDetailsViewOpen(true);
    } else {
      dispatch(setSelectedScheduleEntry(null));
      if (wasDragged && !isOverlapping) {
        dispatch(
          patchScheduleEntry({
            id: schedule.id,
            data: {
              timeFrom: schedule.timeFrom,
              timeTo: schedule.timeTo,
              weekdayFrom: schedule.weekdayFrom,
              weekdayTo: schedule.weekdayTo,
            },
          }),
        );
      }

      if (wasDragged && isOverlapping && preDragSnapshot != null) {
        dispatch(
          updateDraftScheduleEntry({
            schedule: { id: schedule.id, ...preDragSnapshot },
            undoDrag: true,
          }),
        );
      }
    }
    dragging.current = false;
    startPos.current = null;
    setPreDragSnapshot(null);

    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
  };

  const topMouseDownHandler = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!dragging.current) {
      setPreDragSnapshot({
        timeFrom: schedule.timeFrom,
        timeTo: schedule.timeTo,
        weekdayFrom: schedule.weekdayFrom,
        weekdayTo: schedule.weekdayTo,
      });
      dispatch(
        setSelectedScheduleEntry({
          entry: schedule,
          mouseState: SchedulerControllerMouseState.DragTopOfSchedule,
          day: null,
          time: null,
        }),
      );
      dragging.current = true;
    }
  };

  const bottomMouseDownHandler = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!dragging.current) {
      setPreDragSnapshot({
        timeFrom: schedule.timeFrom,
        timeTo: schedule.timeTo,
        weekdayFrom: schedule.weekdayFrom,
        weekdayTo: schedule.weekdayTo,
      });
      dispatch(
        setSelectedScheduleEntry({
          entry: schedule,
          mouseState: SchedulerControllerMouseState.DragBottomOfSchedule,
          day: null,
          time: null,
        }),
      );
      dragging.current = true;
    }
  };

  const detailsCloseHandler = (closingOnSave?: boolean) => {
    setIsDetailsViewOpen(false);
    if (isTemporary && closingOnSave === false) {
      dispatch(deleteDraftScheduleEntry(schedule.id));
    }
    if (isSuccessSetOpen(missionsRestSet)) {
      dispatch(patchMissionsRestSet(successResetResponse(missionsRestSet)));
    } else if (isErrorSetOpen(missionsRestSet)) {
      dispatch(patchMissionsRestSet(errorResetResponse(missionsRestSet)));
    }
  };

  const getTimeDescription = () => {
    const timeFromToString = `${getWeekdayLabel(schedule.weekdayFrom)}, ${getStringFromTimeSpan(schedule.timeFrom)} - 
    ${
      schedule.weekdayFrom === schedule.weekdayTo ? "" : `${getWeekdayLabel(schedule.weekdayTo)}, `
    }${getStringFromTimeSpan(schedule.timeTo)}`;

    return <ScheduleEntrySubTypography>{timeFromToString}</ScheduleEntrySubTypography>;
  };

  const getZoneNames = (tooltip: boolean = false, wrappable: boolean = false, height: number) => {
    if (
      mission == null ||
      mission.mode !== NavigatorName.ZoneCleaning ||
      mission.selectedZoneIds.length === 0 ||
      (!tooltip && height < 24)
    ) {
      return null;
    }
    const zoneNames = [...cleaningZonesIdName].filter(
      (z) => z.name != null && mission.selectedZoneIds.some((id) => id === z.id),
    );
    if (!tooltip && height <= 24 * zoneNames.length) {
      return (
        <ScheduleEntrySubTypography noWrap={!wrappable}>{`${zoneNames.length} ${t(
          "cleaning-zones-plural",
        )}`}</ScheduleEntrySubTypography>
      );
    }
    return zoneNames.map((z) => (
      <ScheduleEntrySubTypography
        key={z.id}
        noWrap
        onClick={(event) => (tooltip ? navToZoneClickHandler(event, z.id) : () => {})}
        className={cx(classes.zoneLinkButton, {
          [classes.zoneLinkButtonActive]: !hasBeenEdited && tooltip,
        })}
      >
        {z.name}
      </ScheduleEntrySubTypography>
    ));
  };

  const getLandmarkName = (height: number) => {
    if (
      mission == null ||
      mission.mode !== NavigatorName.Maintenance ||
      mission.selectedLandmarkIds.length === 0 ||
      height < 24
    ) {
      return null;
    }
    const landmark = landmarksIdName.find((lm) => mission.selectedLandmarkIds.some((id) => id === lm.id));
    if (landmark == null) return null;

    return (
      <ScheduleEntrySubTypography
        noWrap
        onClick={(event) => navToLandmarkClickHandler(event, landmark.id)}
        className={cx(classes.zoneLinkButton, {
          [classes.zoneLinkButtonActive]: !hasBeenEdited,
        })}
      >
        {landmark.name}
      </ScheduleEntrySubTypography>
    );
  };

  const navToZoneClickHandler = (event: React.MouseEvent, zoneId: string) => {
    if (!hasBeenEdited) {
      event.preventDefault();
      dispatch(setSelectedZone({ id: zoneId }));
      dispatch(setZonePlannerMouseState(ZoneControllerMouseState.ShowDetails));
      if (isWebApp) {
        navigate(`/room-planner?${FilterKeys.RoomId}=${mission?.room}`);
      } else {
        setFilterParams((prevState) => {
          prevState.set(FilterKeys.Tab, SphereRobotTabs.MapPlanner);
          return prevState;
        });
      }
    }
  };

  const navToLandmarkClickHandler = (event: React.MouseEvent, landmarkId: string) => {
    if (!hasBeenEdited) {
      event.preventDefault();
      dispatch(setSelectedLandmark(landmarkId));
      dispatch(setZonePlannerMouseState(ZoneControllerMouseState.ShowDetails));
    }
  };

  return (
    <React.Fragment>
      {getScheduleBoxPositionsFromSchedule(
        schedule.timeFrom,
        schedule.weekdayFrom,
        schedule.timeTo,
        schedule.weekdayTo,
      ).map((position, index) => (
        <LightTooltipWithBorder
          disableHoverListener={dragging.current}
          arrow={!dragging.current}
          PopperProps={{
            disablePortal: true,
            popperOptions: {
              modifiers: [
                {
                  name: "preventOverflow",
                  options: {
                    enabled: true,
                    boundariesElement: "window", // where "window" is the boundary
                  },
                },
              ],
            },
          }}
          title={
            dragging.current ? undefined : (
              <React.Fragment>
                {position.height >= 24 && (
                  <ScheduleEntryTypography variant="body2" noWrap>
                    {mission == null ? t("new-schedule-entry") : mission.name}
                  </ScheduleEntryTypography>
                )}
                <ScheduleEntrySubTypography>{getNavigatorDescription(mission?.mode ?? "")}</ScheduleEntrySubTypography>
                {getTimeDescription()}
                {getZoneNames(true, true, position.height)}
                {getLandmarkName(position.height)}
                <ScheduleEntrySubTypography>{t("longpress-to-drag")}</ScheduleEntrySubTypography>
              </React.Fragment>
            )
          }
          placement={position.tooltipPlacement}
          sx={{ pointerEvents: "initial", margin: dragging.current ? undefined : "6px" }}
          key={index}
        >
          <div
            className={cx(classes.schedulerBox, {
              [classes.isDragging]: dragging.current,
              [classes.scheduleDisabled]: !schedule.enabled,
              [classes.invalidEntry]: isOverlapping,
              [classes.maintenanceSchedule]: mission?.mode === NavigatorName.Maintenance,
            })}
            style={{
              top: `${position.top}px`,
              left: `calc(100% / 7 * ${position.columnsLeft})`,
              height: `${position.height - 1}px`, // -1 to account for the cell border
              pointerEvents: "auto",
              cursor: dragging.current ? "grabbing" : "pointer",
              backgroundColor:
                isOverlapping || !schedule.enabled || mission?.mode === NavigatorName.Maintenance
                  ? undefined
                  : getColorFromString(
                      mission?.color,
                      currentRoomId != null && mission?.room !== currentRoomId
                        ? "0.07"
                        : isHovering || dragging.current
                          ? "0.5"
                          : "0.3",
                    ),
              borderLeftColor:
                isOverlapping || !schedule.enabled
                  ? undefined
                  : getColorFromString(
                      mission?.color,
                      currentRoomId != null && mission?.room !== currentRoomId ? "0.3" : "1",
                    ),
              zIndex: isOverlapping ? 1 : undefined,
              touchAction: "none",
            }}
            onMouseOver={mouseOverHandler}
            onMouseLeave={mouseLeaveHandler}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={mouseUpTouchEndHandler}
            onContextMenu={(e) => e.preventDefault()}
          >
            {position.height >= parseInt(hourPixelHeight, 10) / 2 && mission?.mode !== NavigatorName.Maintenance && (
              <ScheduleEntryTypography
                variant="body2"
                noWrap
                style={{ display: "flex", alignItems: "center", color: schedule.enabled ? undefined : "grey" }}
              >
                {isOverlapping && <WarningIcon fontSize="small" />}
                {mission == null
                  ? t("new-schedule-entry")
                  : `${mission.name} - ${getNavigatorDescription(mission.mode)}`}
              </ScheduleEntryTypography>
            )}

            {!dragging.current && position.includesStart && matchesSmUp && (
              <React.Fragment>
                <div
                  className={cx(classes.scheduleEntryResizeDiv, classes.scheduleEntryResizeTopDiv)}
                  onMouseDown={topMouseDownHandler}
                />
                {isHovering && (
                  <span className={cx(classes.hoveredScheduleEntryDivDot, classes.hoveredScheduleEntryDivTop)} />
                )}
              </React.Fragment>
            )}
            {!dragging.current && position.includesEnd && matchesSmUp && (
              <React.Fragment>
                <div
                  className={cx(classes.scheduleEntryResizeBottomDiv, classes.scheduleEntryResizeDiv)}
                  onMouseDown={bottomMouseDownHandler}
                />
                {isHovering && (
                  <span className={cx(classes.hoveredScheduleEntryDivDot, classes.hoveredScheduleEntryDivBottom)} />
                )}
              </React.Fragment>
            )}
          </div>
        </LightTooltipWithBorder>
      ))}
      {isDetailsViewOpen && currentRoomId != null && !isOverlapping && (
        <ScheduleEntryForm
          onDialogClose={detailsCloseHandler}
          scheduleEntry={schedule}
          isTemporary={isTemporary}
          roomId={mission?.room ?? currentRoomId}
        />
      )}
    </React.Fragment>
  );
};

export default memo(ScheduleEntryBox);
